<template>
	<div class="max-w-4xl px-4 mx-auto lg:px-0">
		<div class="flex pt-8 pb-4">
			<NuxtLink
				:to="localePath('/blog')"
				class="text-mx-orange hover:underline underline-offset-2 decoration-2 decoration-mx-orange"
			>
				&lt; {{ $t('back-to-blog') }}
			</NuxtLink>
		</div>
		<div class="w-full aspect-w-16 aspect-h-9">
			<NuxtImg
				v-if="hasImage"
				:src="`${imageSrc}/m/324x162`"
				:srcset="`${imageSrc}/m/324x162 324w, ${imageSrc}/m/500x250 500w, ${imageSrc}/m/800x400 800w, ${imageSrc}/m/1000x500 1000w`"
				:alt="article.image?.alt"
				class="object-cover w-full rounded-lg"
			/>
			<div
				v-else
				class="w-full overflow-hidden border border-gray-200 rounded-lg shadow hover:shadow-lg aspect-w-16 aspect-h-9"
			>
				<div class="flex items-center content-center w-full justify-items-center">
					<LoadingSpinner class="h-32 mx-auto text-gray-300" />
				</div>
			</div>
		</div>
		<div
			v-if="hasDate"
			class="pt-4 text-gray-400"
		>
			{{ getDate(dateTime, locale) }}
		</div>
		<div class="mx-auto mb-12">
			<h1 class="py-12 mb-4 text-3xl font-bold lg:text-4xl">
				{{ article?.title }}
			</h1>
			<div
				class="min-w-full prose lg:prose-lg"
				v-html="resolvedRichText"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { BASE_URL, STORYBLOK_VERSION, COMPANY_NAME } from '@/constants/index.js'
import { JSON_LD_LOGO } from '@/constants/json-ld'
import type { BlogArticleProps } from '@/components/blog/types'
import { getDate } from '@/utils/getDate'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

const { getStory } = useStoryblokWithErrorReporting()

const route = useRoute()
const { locale } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

const story = await getStory(`blog/${route?.params?.article}`, {
	version: STORYBLOK_VERSION,
	language: locale.value
})

const { title, description } = story?.value?.content?.metatags

const article = computed<BlogArticleProps>(() => {
	return story.value?.content
})

const hasImage = computed<boolean>(() => {
	return !!article.value?.image
})

const dateTime = computed<string>(() => {
	return article.value?.dateTime || ''
})

const hasDate = computed<boolean>(() => {
	return !!dateTime.value
})

const imageSrc = computed(() => {
	return article.value?.image?.filename || ''
})

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => imageSrc.value,
	ogUrl: () => `${BASE_URL}${route?.fullPath}`,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => imageSrc.value,
	twitterCard: () => 'summary_large_image'
})

useHead({
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicon.ico'
		}
	]
})

definePageMeta({
	h1Elsewhere: true
})

const resolvedRichText = computed(() => renderRichText(article.value.content))

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: BASE_URL
				}, {
					'@type': 'ListItem',
					position: 2,
					name: 'Blog',
					item: `${BASE_URL}/blog`
				}, {
					'@type': 'ListItem',
					position: 3,
					name: title,
					item: `${BASE_URL}${route?.fullPath}`
				}
			]
		},
		{
			'@type': 'BlogPosting',
			mainEntityOfPage: {
				'@type': 'WebPage',
				'@id': `${BASE_URL}${route?.fullPath}`
			},
			headline: title,
			image: imageSrc.value,
			description,
			author: {
				'@type': 'Organization',
				name: COMPANY_NAME,
				url: BASE_URL
			},
			publisher: {
				'@type': 'Organization',
				name: COMPANY_NAME,
				logo: {
					'@type': 'ImageObject',
					url: JSON_LD_LOGO
				}
			},
			datePublished: dateTime.value?.split(' ')[0] // split "2023-09-10" from "2023-09-10 00:00:00"
		}
	]
})
</script>
